import { httpGet, HTTPDestinationType } from '@/app/lib/adapters/httpAdapter'
import { getEnvVariable } from './getenvService'
import { PartyTypeCd } from '../../lib/constants/permissions'
import {
	PerformanceMetricsApiData,
	ListingMetricsApiData,
	AgentPerformanceApiData,
	AgentQuartileMetricsApiData,
	CompassPocListingsApiData,
	CompassPocListingsSummaryApiData,
	CompassPocListingsBreakdownApiData,
} from './models/performanceMetrics'
import { BenchmarkScaleParam, TransactionSortCd, DateRangeCd } from '@/lib/enums/bi-enums'
import { ListingsApiData, TransactionsApiData, ListingsQueryParams } from './models/biListings'
import { getListingStartDate, getListingEndDate, getDateStr } from '../utilities/dateFormatter'
import { assertApiErrorOrThrow } from '../lib/errors/apiError'
import { CategoryDetailsResponse, SendAIAssistantsRequest, SendAIAssistantsResponse } from './models/aiAssistants'
import mockCategoryDetailsData from '@/lib/constants/aiAssistantsMock.json'
import { httpPost } from '@/app/lib/adapters/httpAdapter'

export {
	getProspectiveAgents,
	getProspectiveAgent,
	getProspectiveAgentActivity,
	getMLSListings,
	getProspectiveAgentOffices,
	getProspectiveAgentBrokerageHistory,
	getZipCodeSuggestions,
	getBrokerageNameSuggestions,
	getMLSSelectSuggestions,
	getMLSBrandSelectSuggestions,
	fetchRecruitingActivityData,
	logRecruitingActivity,
	editRecruitingActivity,
	deleteRecruitingActivity,
	addProspectToPipeline,
	updateRecruitingProspect,
	getRecrProspects,
	getRecrProspectsCounts,
	getPipelineStages,
	getOffices,
	getBrokeragePeople,
	getPipelineStageReasons,
	getbrokeragePersonDetailsbyRepId,
	getCompanyOffices,
	getIncentiveRatios,
} from '@agent360/services/agent360Service'
export type { OfficeListQueryParams } from '@agent360/services/agent360Service'

/**
 * Retrieves performance metrics data from the external API and filters by the specified metric period type.
 *
 * @param queryParams - Record containing query parameters for the API request
 * @param token - Optional authentication token for the API request
 * @param metricPeriodTypeCd - The metric period type code to filter by (defaults to 'MONTH')
 * @returns Promise that resolves to filtered performance metrics data containing GCI, sides, and sales volume metrics
 * @throws Error when API request fails or when an error occurs during data processing
 *
 */
export async function getPerformanceMetrics(
	queryParams: Record<string, string | BenchmarkScaleParam>,
	token?: string
): Promise<PerformanceMetricsApiData> {
	let response
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/performancemetrics`, {
			query: queryParams,
			token,
		})

		return response as PerformanceMetricsApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Performance Metrics')
	}
}

export async function getListingsMetrics(queryParams: Record<string, string>): Promise<ListingMetricsApiData> {
	let response
	const queryWithDates = {
		...queryParams,
		fromDt: getDateStr(getListingStartDate()) || '',
		toDt: getDateStr(getListingEndDate()) || '',
	}
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = (await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/listingmetrics`, {
			query: queryWithDates,
		})) as ListingMetricsApiData

		return response
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Listing Metrics')
	}
}

export async function getListingsMetricsDownloadUrl(
	queryParams: Record<string, string>,
	token?: string
): Promise<{ downloadUrl: string; expiresIn: number }> {
	const queryWithDates = {
		fromDt: getDateStr(getListingStartDate()) || '',
		toDt: getDateStr(getListingEndDate()) || '',
		...queryParams,
	}
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/listingmetrics/report`

		const response = await httpGet(HTTPDestinationType.EXTERNAL, url, {
			query: queryWithDates,
			token,
		}, true)
		return response as { downloadUrl: string; expiresIn: number }
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Listing Metrics Download URL')
	}
}

export async function getTransactionMetricsDownloadUrl(
	queryParams: Record<string, string>,
	token?: string
): Promise<{ downloadUrl: string; expiresIn: number }> {
	const queryWithDates = {
		fromDt: getDateStr(getListingStartDate()) || '',
		toDt: getDateStr(getListingEndDate()) || '',
		...queryParams,
	}
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/transactionmetrics/report`

		const response = await httpGet(HTTPDestinationType.EXTERNAL, url, {
			query: queryWithDates,
			token,
		}, true)
		return response as { downloadUrl: string; expiresIn: number }
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Transaction Metrics Download URL')
	}
}

/**
 * Retrieves listings data from the API.
 *
 * @param queryParams - Record containing query parameters for the API request
 * @param token - Optional authentication token for the API request
 * @returns Promise that resolves to your listings data
 * @throws Error when API request fails or when an error occurs during data processing
 */
export async function getListingsData(
	queryParams: ListingsQueryParams,
	token?: string,
	signal?: AbortSignal
): Promise<ListingsApiData> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/listingmetrics/details`, {
			query: queryParams,
			token,
			signal,
		})
		return response as ListingsApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Listings Data')
	}
}

/**
 * Interface for transaction details query parameters
 */
export interface TransactionDetailsQueryParams {
	partyId: string
	partyTypeCd: PartyTypeCd
	fromDt: string
	toDt: string
	transactionStatusCds?: string
	pageNo?: number
	pageSz?: number
	sortCd: TransactionSortCd
	dateRangeCd: DateRangeCd
}

/**
 * Retrieves transaction details data from the API for deal pipeline and past estimated close date tables.
 *
 * @param queryParams - Transaction details query parameters for the API request
 * @param token - Optional authentication token for the API request
 * @returns Promise that resolves to transaction details data
 * @throws Error when API request fails or when an error occurs during data processing
 */
export async function getTransactionDetails(
	queryParams: TransactionDetailsQueryParams,
	token?: string
): Promise<TransactionsApiData> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/transactionmetrics/details`, {
			query: queryParams,
			token,
		})
		return response as TransactionsApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Transaction Data')
	}
}

/**
 * Retrieves AI assistant category details with questions for a specific category.
 * Currently returns mock data for development/testing purposes.
 *
 * @param categoryName - The name of the category to fetch details for
 * @param token - Optional authentication token for the API request (unused in mock implementation)
 * @returns Promise that resolves to category details with questions
 * @throws Error when category is not found in mock data
 */
export async function getCategoryDetails(_categoryName: string, _token?: string): Promise<CategoryDetailsResponse> {
	try {
		return mockCategoryDetailsData as CategoryDetailsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET AI Category Details')
	}
}

export async function getAgentPerformanceMetrics(
	queryParams: Record<string, string | BenchmarkScaleParam>,
	token?: string
): Promise<AgentPerformanceApiData> {
	let response
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/agentproductivity/summary`, {
			query: queryParams,
			token,
		})

		return response as AgentPerformanceApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Performance Metrics')
	}
}

export async function getAgentQuartileMetrics(
	queryParams: Record<string, string>,
	token?: string
): Promise<AgentQuartileMetricsApiData> {
	let response
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/agentproductivity/quartiles`, {
			query: queryParams,
			token,
		})

		return response as AgentQuartileMetricsApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Quartile Metrics')
	}
}

export async function sendAIAssistants(payload: SendAIAssistantsRequest, token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/aiassistants`
		const response = await httpPost(HTTPDestinationType.EXTERNAL, url, {
			body: payload,
			token,
		})
		return response as SendAIAssistantsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error sending AI Assistants request')
	}
}

export async function sendAIAssistantsChat(payload: SendAIAssistantsRequest, token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const url = `${apiBaseUrl}/api/aiassistantschat`
		const response = await httpPost(HTTPDestinationType.EXTERNAL, url, {
			body: payload,
			token,
		})
		return response as SendAIAssistantsResponse
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error sending AI Assistants Chat request')
	}
}

export async function getCompassPocListings(token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/compass-poc/listings`, {
			token,
		})
		return response as CompassPocListingsApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Compass POC Listings')
	}
}

export async function getCompassPocListingsSummary(token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(HTTPDestinationType.EXTERNAL, `${apiBaseUrl}/api/compass-poc/listings-summary`, {
			token,
		})
		return response as CompassPocListingsSummaryApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Compass POC Listings Summary')
	}
}

export async function getCompassPocListingsBreakdown(token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/compass-poc/listings-breakdown`,
			{
				token,
			}
		)
		return response as CompassPocListingsBreakdownApiData
	} catch (error: unknown) {
		return assertApiErrorOrThrow(error, 'Error GET Compass POC Listings Breakdown')
	}
}
